import { useState, useEffect, useCallback } from "react";
import { useWebClientApi } from "src/api";
import { useUser, useAuth } from "src/context";

export interface DealerEmployee {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  last_activity?: string;
}

interface UseDealerEmployeesReturn {
  employees: DealerEmployee[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useDealerEmployees = (): UseDealerEmployeesReturn => {
  const { user: firebaseUser } = useAuth();
  const { userData } = useUser();
  const api = useWebClientApi();
  const [employees, setEmployees] = useState<DealerEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployees = useCallback(async () => {
    const dealerId = userData?.dealer_id;
    if (!dealerId || !firebaseUser) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.getEmployees();
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching dealer employees:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch employees",
      );
    } finally {
      setLoading(false);
    }
  }, [api, userData?.dealer_id, firebaseUser]);

  useEffect(() => {
    void fetchEmployees();
  }, [fetchEmployees]);

  return {
    employees,
    loading,
    error,
    refetch: () => void fetchEmployees(),
  };
};
